/**
 * Hostel catalogue, as published at bietdvg.edu/facilities/boys_hostel and
 * /facilities/ladies_hostel (fetched 2026-08-03). Slug used at /hostels/:slug.
 *
 * Where the source contradicts itself, this file keeps both readings rather than
 * silently reconciling them, and labels each:
 *  · `facts` are the quick-fact bullets the page lists, in its own words.
 *  · `blocks` is the itemised block table. It is internally consistent but does not
 *    always reach the round figure the prose quotes — the boys page says "three
 *    blocks accommodating about 510 boarders" while itemising two blocks totalling
 *    456, so a third block appears not to be broken out.
 *
 * Fees carry the year the source labels them with. The boys figure is current
 * (2025-26); the ladies figure is the most recent that page gives (2021-22) — its
 * other, undated table quotes a different structure (₹30,000/yr plus ₹2,100/month
 * mess), so the year label matters and is shown in the UI.
 *
 * The bank account number and IFSC the source publishes for fee payment are
 * deliberately NOT reproduced here.
 */
import type { Media } from "@zivin-labs/site-content";

/** One residential block, as itemised in the source's block table. */
export interface HostelBlock {
  name: string;
  rooms: number;
  boarders: number;
}

export interface HostelFeeRow {
  particular: string;
  amount: string;
}

export interface HostelContact {
  name: string;
  role: string;
  /** Mobile number as published; absent where the source shows "--". */
  phone?: string;
}

export interface Hostel {
  slug: string;
  name: string;
  /** Short tag, e.g. "BOYS". */
  short: string;
  /** "Boys'" | "Ladies'". */
  gender: string;
  estd: number;
  intro: string[];
  /** Quick facts exactly as the source lists them. */
  facts: Array<{ label: string; value: string }>;
  blocks: HostelBlock[];
  amenities: string[];
  fee: { year: string; rows: HostelFeeRow[]; total: string };
  admission: string[];
  roomRules: string[];
  contacts: HostelContact[];
  photos?: Media[];
  /** Caution published on both source pages. */
  note?: string;
}

const VALUABLES_NOTE = "Students are advised not to keep valuable materials in the room.";

export const HOSTELS: Hostel[] = [
  {
    slug: "boys-hostel",
    name: "BIET Boys Hostel",
    short: "BOYS",
    gender: "Boys'",
    estd: 1984,
    intro: [
      "The Hostel is founded in 1984 to accommodate the boys, who come from different parts of the world. The rooms are well furnished and the surroundings of hostel is very green and pleasant, which encourages the students to study during late nights. The students who stayed in the hostel have secured 18 university ranks right from the inception of the hostel.",
      "The hostel is having two dining halls of North & South Indian to cater the needs of the boarders for their taste. Hostel is known for its discipline and well managed by dedicated faculty & staff members.",
    ],
    facts: [
      { label: "Started", value: "1984" },
      { label: "Blocks", value: "3" },
      { label: "Rooms", value: "200" },
      { label: "Hostel area", value: "4.5 acres · 12,877.09 sq ft built up" },
    ],
    blocks: [
      { name: "Kamadhenu", rooms: 72, boarders: 216 },
      { name: "Kalpataru", rooms: 80, boarders: 240 },
    ],
    amenities: [
      "Three colour T.V sets for entertainment & information",
      "“EKALAVYA” indoor games hall for shuttle, badminton, table tennis, carrom & chess",
      "CCTV coverage",
      "Multi gym for physical fitness",
      "Vinayaka reading hall",
      "Reading room with Kannada & English newspapers, periodicals & magazines",
      "Captive power — 125 KvA",
      "Solar hot water for each block & kitchen geyser hot water",
      "Drinking water (R.O) & softener plant",
      "Deep freezer",
      "Vehicle parking",
      "Wardrobe in each room",
      "Banking facility near by campus",
    ],
    fee: {
      year: "2025-26",
      rows: [
        { particular: "Hostel fee (including mess charge)", amount: "₹75,000" },
        { particular: "Deposit (refundable)", amount: "₹10,000" },
      ],
      total: "₹85,000",
    },
    admission: [
      "The student & parent/guardian should meet the warden along with his C.E.T. identity card & college fee receipt.",
      "Application form shall be collected in the Hostel Office showing the Warden’s consent letter.",
      "After duly filling application form get the signature of the admission clerk and the Principal before obtaining the Warden’s signature.",
      "The pay order shall be taken in the name of the B.I.E.T Campus Hostel, Davangere.",
    ],
    roomRules: [
      "Three students will be accommodated in each room.",
      "Every student will be provided with a cot, a table and a chair.",
      "Rooms will be allotted by the Warden/Deputy Warden.",
      "Students should bring their own bed, bedspread, blanket, mosquito net, bucket and mug.",
    ],
    contacts: [
      { name: "Prof. Y. Vrushabhendrappa", role: "Director" },
      { name: "Dr. H.B. Aravind", role: "Principal" },
      { name: "Dr. G. Manavendra", role: "Warden", phone: "9964030806" },
      { name: "Dr. G H Kiran Kumar", role: "Deputy Warden", phone: "9343031014" },
      { name: "Dr. Raghu M E", role: "Deputy Warden", phone: "9741646266" },
      { name: "Dr. Anil Kumar K N", role: "Deputy Warden", phone: "9743447591" },
      { name: "Smt. Bhagya Shanthkumar", role: "Deputy Warden", phone: "9844124330" },
      { name: "K. Rudrappa", role: "Manager", phone: "9964378728" },
    ],
    note: VALUABLES_NOTE,
  },
  {
    slug: "ladies-hostel",
    name: "Amrutha Ladies Hostel",
    short: "LADIES",
    gender: "Ladies'",
    estd: 2008,
    intro: [
      "The Hostel was started in 2008 to accommodate BIET girl students, who come from different parts of the world. The rooms are well furnished and the surroundings of hostel is very green and pleasant, which encourages the students to study. The hostel is spacious with a built up area of 5029 Sq. Mts. with 104 rooms accommodating 320 students.",
      "The Chairman of “AMRUTHA”, Dr. S.S.D.J Ladies Hostel is Dr Prabha Mallikarjun, Member, BEA, Davangere. The hostel is well managed by a team of experienced office staff.",
      "North Indian & south Indian dishes are prepared in the hostel mess, which is managed by the hostel administration itself. Ragging is totally prohibited in the hostel. For entertainment and refreshment the hostel has an indoor shuttle court and an indoor sports hall used for table tennis, chess, carrom and other indoor games, and a common TV hall open for limited hours.",
      "A stationery shop is provided in the hostel premises and a walking track is laid all around the hostel, which is surrounded by a landscaped garden. All the national festivals are celebrated in the hostel, and the boarders take an active part in freshers’ day, Ganesh Chaturthi, Holi, Diwali and the farewell function held every year.",
    ],
    facts: [
      { label: "Started", value: "2008" },
      { label: "Blocks", value: "2" },
      { label: "Rooms", value: "104" },
      { label: "Built up area", value: "5,029 sq m" },
    ],
    blocks: [
      { name: "Amrutha", rooms: 104, boarders: 320 },
      { name: "Kalyana", rooms: 52, boarders: 156 },
      { name: "Hemavathi", rooms: 72, boarders: 280 },
    ],
    amenities: [
      "Two dining halls serving North Indian & South Indian dishes",
      "Mess managed by the hostel administration itself",
      "Indoor shuttle court",
      "Indoor sports hall — table tennis, chess, carrom and other indoor games",
      "Common TV hall, open for limited hours",
      "Stationery shop within the hostel premises",
      "Walking track laid around the hostel",
      "Landscaped garden",
      "Ragging totally prohibited",
    ],
    fee: {
      year: "2021-22",
      rows: [
        { particular: "Hostel fee (including mess charge)", amount: "₹65,000" },
        { particular: "Deposit (refundable)", amount: "₹10,000" },
      ],
      total: "₹75,000",
    },
    admission: [
      "The student & parent/guardian should meet the warden along with her C.E.T. identity card & college fee receipt.",
      "Application form shall be collected in the Hostel Office showing the Warden’s consent letter.",
      "After duly filling application form the student should get the signature of the admission clerk, Director and the Principal before obtaining the Warden’s signature.",
      "The duly filled application form along with two passport size photographs & a pay order of ₹30,000 from Bapuji Co-op Bank must be submitted.",
      "The pay order shall be taken in the name of the Dr. S.S.D.J Ladies Hostel, Davangere.",
    ],
    roomRules: [
      "Three students will be accommodated in each room.",
      "Every student will be provided with a cot, bed, a table and a chair.",
      "Rooms will be allotted by the Warden/Deputy Warden.",
      "Students should bring their own bedspread, blanket, mosquito net, bucket and mug.",
    ],
    contacts: [
      { name: "Prof. Y. Vrushabhendrappa", role: "Director", phone: "9448393144" },
      { name: "Dr. H.B. Aravind", role: "Principal", phone: "9844061789" },
      { name: "Dr. S.M. Chandrashekara", role: "Warden", phone: "9844422882" },
      { name: "Dr. Vinutha H.P.", role: "Deputy Warden", phone: "9964262478" },
      { name: "Smt. Nirmala G.", role: "Deputy Warden", phone: "9964618228" },
    ],
    photos: [
      { url: "sites/biet/hostels/amrutha-ladies-hostel-exterior.jpg", alt: "Amrutha Ladies Hostel building exterior", width: 983, height: 484 },
      { url: "sites/biet/hostels/amrutha-dining-hall.jpg", alt: "Dining hall at the Amrutha Ladies Hostel", width: 983, height: 484 },
      { url: "sites/biet/hostels/amrutha-room-wardrobes.jpg", alt: "Built-in wardrobes in a hostel room", width: 983, height: 484 },
    ],
    note: VALUABLES_NOTE,
  },
];

export function getHostel(slug: string): Hostel | undefined {
  return HOSTELS.find((h) => h.slug === slug);
}

/** Rooms across the itemised blocks — not always the round figure the prose quotes. */
export function totalRooms(h: Hostel): number {
  return h.blocks.reduce((n, b) => n + b.rooms, 0);
}

/** Boarders across the itemised blocks. */
export function totalBoarders(h: Hostel): number {
  return h.blocks.reduce((n, b) => n + b.boarders, 0);
}
